<template>
  <div class="layout layout-v2">
    <app-watermark :user-name="user.name" />
    <app-sidebar ref="sidebar" :active-key="activeKey" />

    <div class="main">
      <header class="topbar topbar-v2">
        <h1 class="page-title">{{ pageTitle }}</h1>
        <div class="right">
          <div class="period">
            <span class="p-cap">统计区间</span>
            <div class="seg">
              <button
                v-for="p in periods"
                :key="p.key"
                class="seg-btn"
                :class="{ active: period === p.key }"
                @click="onPeriodChange(p.key)"
              >
                {{ p.label }}
              </button>
            </div>
            <a-range-picker
              v-if="period === 'custom'"
              v-model="customRange"
              class="period-range-picker"
              value-format="YYYY-MM-DD"
              :placeholder="['开始日期', '结束日期']"
              :allow-clear="false"
              @change="onCustomRangeChange"
            />
          </div>
          <router-link to="/reminder" class="bell-link">
            <svg class="bell-svg" width="18" height="18" viewBox="0 0 20 20" fill="none">
              <path d="M10 3a4 4 0 014 4v3.5l1.5 2.5H4.5L6 10.5V7a4 4 0 014-4z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
              <path d="M8.5 16a1.5 1.5 0 003 0" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            <span v-if="todoCount" class="bell-badge">{{ todoCount > 99 ? '99+' : todoCount }}</span>
          </router-link>
          <div class="user-pill">
            <span class="avatar">{{ user.avatar || '系' }}</span>
            <span class="user-name">{{ user.name || '系统管理员' }}</span>
          </div>
          <button type="button" class="logout-btn" @click="openChangePassword">修改密码</button>
          <button type="button" class="logout-btn" @click="onLogout">退出</button>
        </div>
      </header>
      <div class="content" :class="{ 'content-dashboard': isDashboard }">
        <router-view :key="$route.fullPath" />
      </div>
    </div>

    <a-modal v-model="pwdVisible" title="修改密码" width="520px" destroy-on-close @ok="submitChangePassword">
      <a-form :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
        <a-form-item label="旧密码" required>
          <a-input-password v-model="pwdForm.oldPassword" placeholder="请输入当前密码" />
        </a-form-item>
        <a-form-item label="新密码" required>
          <a-input-password v-model="pwdForm.newPassword" :placeholder="passwordHint" />
          <div class="pwd-hint">{{ passwordHint }}</div>
        </a-form-item>
        <a-form-item label="确认密码" required>
          <a-input-password v-model="pwdForm.confirm" placeholder="再次输入新密码" />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import AppSidebar from '@/components/AppSidebar.vue'
import AppWatermark from '@/components/AppWatermark.vue'
import { authApi } from '@/api/biz'
import { formatPeriodLabel, dispatchPeriodChange } from '@/utils/period'
import { PASSWORD_HINT, validatePasswordStrength } from '@/utils/passwordPolicy'

function defaultCustomRange() {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return [`${y}-01-01`, `${y}-${m}-${d}`]
}

export default {
  name: 'MainLayout',
  components: { AppSidebar, AppWatermark },
  data() {
    return {
      periods: [
        { key: 'month', label: '本月' },
        { key: 'quarter', label: '本季' },
        { key: 'year', label: '本年' },
        { key: 'custom', label: '自定义' }
      ],
      customRange: [],
      todoCount: 0,
      passwordHint: PASSWORD_HINT,
      pwdVisible: false,
      pwdForm: { oldPassword: '', newPassword: '', confirm: '' }
    }
  },
  computed: {
    ...mapState(['user', 'period', 'periodStart', 'periodEnd']),
    activeKey() {
      return this.$route.meta.menuKey || ''
    },
    pageTitle() {
      return this.$route.meta.title || ''
    },
    isDashboard() {
      return this.$route.name === 'dashboard'
    }
  },
  created() {
    this.syncCustomRangeFromStore()
  },
  methods: {
    syncCustomRangeFromStore() {
      if (this.periodStart && this.periodEnd) {
        this.customRange = [this.periodStart, this.periodEnd]
      } else if (this.period === 'custom') {
        this.customRange = defaultCustomRange()
        this.$store.dispatch('setPeriodRange', { start: this.customRange[0], end: this.customRange[1] })
      }
    },
    emitPeriodChange() {
      const label = formatPeriodLabel(this.period, this.periodStart, this.periodEnd)
      dispatchPeriodChange({
        period: this.period,
        startDate: this.periodStart,
        endDate: this.periodEnd,
        label
      })
    },
    onPeriodChange(key) {
      this.$store.dispatch('setPeriod', key)
      if (key === 'custom') {
        if (!this.periodStart || !this.periodEnd) {
          this.customRange = defaultCustomRange()
          this.$store.dispatch('setPeriodRange', { start: this.customRange[0], end: this.customRange[1] })
        } else {
          this.customRange = [this.periodStart, this.periodEnd]
        }
      }
      const label = formatPeriodLabel(key, this.periodStart, this.periodEnd)
      this.$message.info(`统计区间已切换为「${label}」`)
      this.emitPeriodChange()
    },
    onCustomRangeChange(dates) {
      if (!dates || dates.length !== 2) return
      this.$store.dispatch('setPeriod', 'custom')
      this.$store.dispatch('setPeriodRange', { start: dates[0], end: dates[1] })
      const label = formatPeriodLabel('custom', dates[0], dates[1])
      this.$message.info(`统计区间已切换为「${label}」`)
      this.emitPeriodChange()
    },
    async onLogout() {
      try {
        await authApi.logout()
      } catch (e) {
        /* ignore */
      }
      this.$store.dispatch('logout')
      this.$router.push('/login')
    },
    openChangePassword() {
      this.pwdForm = { oldPassword: '', newPassword: '', confirm: '' }
      this.pwdVisible = true
    },
    submitChangePassword() {
      const err = validatePasswordStrength(this.pwdForm.newPassword)
      if (err) {
        this.$message.warning(err)
        return Promise.reject(new Error('validation'))
      }
      if (this.pwdForm.newPassword !== this.pwdForm.confirm) {
        this.$message.warning('两次输入的新密码不一致')
        return Promise.reject(new Error('validation'))
      }
      if (!this.pwdForm.oldPassword) {
        this.$message.warning('请输入旧密码')
        return Promise.reject(new Error('validation'))
      }
      return authApi.changePassword({
        oldPassword: this.pwdForm.oldPassword,
        newPassword: this.pwdForm.newPassword
      }).then(async () => {
        await this.$store.dispatch('refreshUser')
        this.$message.success('密码已修改')
        this.pwdVisible = false
      }).catch((e) => {
        return Promise.reject(e)
      })
    }
  }
}
</script>

<style scoped>
.bell-link {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  color: #6b7280;
  transition: background 0.2s;
}
.bell-link:hover {
  background: #f5f5f5;
  color: #374151;
}
.bell-svg { display: block; }
.bell-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: #ff4d4f;
  color: #fff;
  font-size: 10px;
  line-height: 16px;
  text-align: center;
  font-weight: 600;
}
.user-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px 4px 4px;
  border-radius: 20px;
}
.user-name { font-size: 14px; color: rgba(0, 0, 0, 0.85); }

.logout-btn {
  height: 32px;
  padding: 0 14px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: #ffffff;
  color: #595959;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s;
}
.logout-btn:hover {
  color: #ff4d4f;
  border-color: #ffccc7;
  background: #fff2f0;
}
.period-range-picker {
  width: 240px;
}
.period-range-picker >>> .ant-calendar-picker-input {
  height: 32px;
  font-size: 13px;
}
.pwd-hint { color: #8c8c8c; font-size: 12px; margin-top: 4px; }
</style>
